import { useWhatsAppNumbers } from '@restaurant/api-client/react';
import { Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { formatPhone } from '@/lib/utils';

const QUALITY: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'muted' }> = {
  GREEN: { label: 'Quality: high', variant: 'success' },
  YELLOW: { label: 'Quality: medium', variant: 'warning' },
  RED: { label: 'Quality: low', variant: 'destructive' },
};

/** Numbers connected through the WhatsApp gateway, with Meta's status when available. */
export function WhatsAppNumbersCard() {
  const { data, isLoading } = useWhatsAppNumbers();
  return (
    <Card>
      <CardHeader>
        <CardTitle>WhatsApp numbers</CardTitle>
        <CardDescription>
          The WhatsApp Business numbers connected to this app{data?.wabaId ? ` (account ${data.wabaId})` : ''}. Messages go out from the default number.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {isLoading ? <Skeleton className="h-16" /> : null}
        {data?.error ? <p className="text-sm text-destructive">{data.error}</p> : null}
        {data && !data.live && !data.error ? (
          <p className="text-xs text-muted-foreground">Meta didn't answer (token or account not set up yet?), so only numbers seen so far are listed, without status details.</p>
        ) : null}
        {data?.numbers.map((n) => {
          const q = n.qualityRating ? QUALITY[n.qualityRating] : undefined;
          return (
            <div key={n.phoneNumberId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm">
              <Phone className="size-4 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{n.displayPhoneNumber ? formatPhone(n.displayPhoneNumber.replace(/\D/g, '')) : `Number id ${n.phoneNumberId}`}</p>
                <p className="text-xs text-muted-foreground">
                  {n.verifiedName ?? 'Name not available'}
                  {n.nameStatus ? ` · name ${n.nameStatus.toLowerCase().replaceAll('_', ' ')}` : ''}
                  {n.messagingLimitTier ? ` · limit ${n.messagingLimitTier.replace('TIER_', '')} customers/day` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {n.isDefault ? <Badge>Default</Badge> : null}
                {q ? <Badge variant={q.variant}>{q.label}</Badge> : null}
                {n.status ? <Badge variant="outline">{n.status.toLowerCase()}</Badge> : null}
              </div>
            </div>
          );
        })}
        {data && !data.numbers.length && !data.error ? <p className="text-sm text-muted-foreground">No numbers connected yet.</p> : null}
      </CardContent>
    </Card>
  );
}
